import { useState, useEffect, useCallback, useMemo } from 'react';
import moment from 'moment';
import { DATE_FORMAT } from 'framework/utils';
import useLeaveBalanceService from '../balancesService';
import { isPolishB2BOrCFSEmployee, useEmployeeService } from 'domain/shared/employee';
import { MappedLeaveItem, Period, ProcessedLeaveYear } from '../types';
import { LoadingParams } from 'framework/list';
import { Filter } from 'framework/list';

const useLeaveBalances = (id: string | null) => {
  const {
    getPaidLeavesBalances,
    getSickDaysBalances,
    getOtherLeavesBalances,
    getPeriods,
    getCurrentPeriod,
    getNextPeriod,
    getFilteredPaidLeaves,
    getFilteredSickDays,
    getFilteredOtherLeaves,
  } = useLeaveBalanceService();
  const { getById } = useEmployeeService();

  const [showAllPeriods, setShowAllPeriods] = useState(false);
  const [currentPeriod, setCurrentPeriod] = useState<Period | null>(null);
  const [nextPeriod, setNextPeriod] = useState<Period | null>(null);
  const [paidLeavesData, setPaidLeavesData] = useState<ProcessedLeaveYear[]>([]);
  const [sickDaysData, setSickDaysData] = useState<ProcessedLeaveYear[]>([]);
  const [otherLeavesData, setOtherLeavesData] = useState<MappedLeaveItem[]>([]);
  const [isPolishContractor, setIsPolishContractor] = useState(false);
  const [isDataReady, setIsDataReady] = useState(false);

  useEffect(() => {
    let isMounted = true;

    if (!id) {
      setCurrentPeriod(null);
      setNextPeriod(null);
      setPaidLeavesData([]);
      setSickDaysData([]);
      setOtherLeavesData([]);
      setIsPolishContractor(false);
      setIsDataReady(false);
      return;
    }

    setIsDataReady(false);

    Promise.all([
      getPeriods(id).then(periods => {
        if (!isMounted) {
          return;
        }

        const current = getCurrentPeriod(periods);
        setCurrentPeriod(current);
        setNextPeriod(current ? getNextPeriod(periods, current) : null);
      }),
      getPaidLeavesBalances(id).then(result => {
        if (isMounted) {
          setPaidLeavesData(result);
        }
      }),
      getSickDaysBalances(id).then(result => {
        if (isMounted) {
          setSickDaysData(result);
        }
      }),
      getOtherLeavesBalances(id).then(result => {
        if (isMounted) {
          setOtherLeavesData(result);
        }
      }),
      getById(id).then(result => {
        if (isMounted) {
          setIsPolishContractor(isPolishB2BOrCFSEmployee(result));
        }
      }),
    ]).finally(() => {
      if (isMounted) {
        setIsDataReady(true);
      }
    });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const filteredPaidLeaves = useMemo(
    () => getFilteredPaidLeaves(paidLeavesData, currentPeriod, nextPeriod, showAllPeriods),
    [currentPeriod, getFilteredPaidLeaves, nextPeriod, paidLeavesData, showAllPeriods],
  );

  const filteredSickDays = useMemo(
    () => getFilteredSickDays(sickDaysData, currentPeriod, nextPeriod, showAllPeriods),
    [currentPeriod, getFilteredSickDays, nextPeriod, showAllPeriods, sickDaysData],
  );

  const getPaidLeavesDataMethod = useCallback(async () => {
    if (!isDataReady) {
      return { result: [] };
    }

    return { result: filteredPaidLeaves };
  }, [filteredPaidLeaves, isDataReady]);

  const getAllPaidLeavesDataMethod = useCallback(async () => {
    if (!isDataReady) {
      return { result: [] };
    }

    return { result: paidLeavesData };
  }, [isDataReady, paidLeavesData]);

  const getSickDaysDataMethod = useCallback(async () => {
    if (!isDataReady) {
      return { result: [] };
    }

    return { result: filteredSickDays };
  }, [filteredSickDays, isDataReady]);

  const getOtherLeavesDataMethod = useCallback(
    async (loadParams: Partial<LoadingParams> = {}) => {
      if (!isDataReady) {
        return { result: [] };
      }

      return {
        result: getFilteredOtherLeaves(otherLeavesData, loadParams),
      };
    },
    [getFilteredOtherLeaves, isDataReady, otherLeavesData],
  );

  const otherLeavesDefaultFilter = useMemo<Filter>(() => {
    const filter: Filter = {};

    if (!showAllPeriods && currentPeriod) {
      filter.startDate = {
        ge: moment(currentPeriod.startDate).format(DATE_FORMAT.API),
      };
    }

    return filter;
  }, [currentPeriod, showAllPeriods]);

  return {
    showAllPeriods,
    setShowAllPeriods,
    currentPeriod,
    nextPeriod,
    isPolishContractor,
    paidLeavesItems: filteredPaidLeaves,
    sickDaysItems: filteredSickDays,
    getPaidLeavesDataMethod,
    getAllPaidLeavesDataMethod,
    getSickDaysDataMethod,
    getOtherLeavesDataMethod,
    otherLeavesDefaultFilter,
    isDataReady,
  };
};

export default useLeaveBalances;
